/**
 * PaymentService — Stripe subscription (Kjuup.io) API layer.
 */

import axios from "axios";
import { callAPI, getFingerprintHeaders } from "../helper/apiUtils";
import appConfig from "../config/appConfig";
import {
  SET_SUBSCRIPTION_LOADING,
  SET_SUBSCRIPTION_STATUS,
} from "../store";
import { clockOffsetFromServer } from "../utils/trialCountdown";

const safeMessage = (e, fallback) =>
  e?.response?.data?.message || e?.message || fallback;

// The shared API client has no timeout because uploads need an open-ended one.
// Billing calls are small JSON round-trips, so bound them: without this a
// stalled request leaves the Subscribe button spinning forever.
const BILLING_OPTIONS = { timeout: 45000 };
export const BILLING_NETWORK_ERROR = "BILLING_NETWORK_ERROR";

export function isBillingNetworkError(e) {
  return e?.code === BILLING_NETWORK_ERROR;
}

async function billingRequest(path, method, body, fallbackMessage) {
  const { data } = await callAPI(path, {}, method, body, BILLING_OPTIONS);
  if (data?.status === true) return data.data;
  const err = new Error(data?.message || fallbackMessage);
  if (data?.code === "NETWORK_ERROR") err.code = BILLING_NETWORK_ERROR;
  throw err;
}

export async function getPlans() {
  const data = await billingRequest("/payment/plans", "GET", null, "Failed to load plans");
  return data?.plans || [];
}

/**
 * Shared Kjuup.io feature labels (Admin /pakete + subscription page).
 * Public endpoint — no JWT (avoids auth refresh side-effects).
 * Browser uses the CRA `/api` proxy so it hits the same backend as the app.
 */
export async function getKjuupIoFeatures() {
  const url =
    typeof window !== "undefined"
      ? "/api/marketing/kjuupIoFeatures"
      : `${appConfig.API_URL}/marketing/kjuupIoFeatures`;

  const { data } = await axios.get(url, {
    timeout: 15000,
    headers: {
      Accept: "application/json",
      ...getFingerprintHeaders(),
    },
  });

  if (data?.status === true) {
    return Array.isArray(data.features) ? data.features : [];
  }
  throw new Error(data?.message || "Failed to load Kjuup.io features");
}

/**
 * Website package prices (Admin Center → Stripe catalogue) and the Kjuup+
 * feature labels, as shown on kjuup.de/pakete. Public endpoint, like
 * getKjuupIoFeatures. Error responses carry placeholder prices, so only a
 * successful payload is returned.
 */
export async function getMarketingPrices() {
  const url =
    typeof window !== "undefined"
      ? "/api/marketing/prices"
      : `${appConfig.API_URL}/marketing/prices`;

  const { data } = await axios.get(url, {
    timeout: 15000,
    headers: {
      Accept: "application/json",
      ...getFingerprintHeaders(),
    },
  });

  if (data?.status === true && data.prices) {
    return {
      prices: data.prices,
      monthlyPremiumPercent: data.monthlyPremiumPercent,
      kjuupPlusFeatures: Array.isArray(data.kjuupPlusFeatures) ? data.kjuupPlusFeatures : [],
    };
  }
  throw new Error(data?.message || "Failed to load package prices");
}

function normalizeUiLangCode(lang) {
  const raw = String(lang || "de").toLowerCase().replace(/_/g, "-");
  if (raw === "gsw" || raw.startsWith("gsw-")) return "gsw";
  if (raw === "wae" || raw.startsWith("wae-")) return "wae";
  return raw.split("-")[0] || "de";
}

/** Pick label for current app language (falls back to EN, then DE). */
export function labelForKjuupIoFeature(feature, language) {
  if (!feature) return "";
  const code = normalizeUiLangCode(language);
  const tmap = feature.translations || {};
  return (
    tmap[code] ||
    (code === "de" || code === "gsw" || code === "wae" ? feature.de : "") ||
    feature.en ||
    tmap.en ||
    feature.de ||
    tmap.de ||
    ""
  );
}

export async function startTrial() {
  return billingRequest(
    "/payment/subscription/start-trial",
    "POST",
    {},
    "Failed to start free trial"
  );
}

export async function subscribe(plan = "plus_monthly") {
  return billingRequest(
    "/payment/subscription/subscribe",
    "POST",
    { plan },
    "Failed to start subscription"
  );
}

export async function confirmSetup({ setupIntentId, subscriptionId } = {}) {
  return billingRequest(
    "/payment/subscription/confirm-setup",
    "POST",
    { setupIntentId, subscriptionId },
    "Failed to confirm payment method"
  );
}

/** @deprecated Prefer startTrial / subscribe */
export async function createSubscription(plan = "plus_monthly") {
  return subscribe(plan);
}

/** Enrich API payload so trial banner always has trialing + daysRemaining. */
export function normalizeSubscriptionStatus(raw) {
  const data = raw || {};
  const subscriptionStatus = data.subscriptionStatus ?? null;
  // API may send trialDaysRemaining (deployed) and/or daysRemaining.
  const rawDays =
    data.daysRemaining != null && data.daysRemaining !== ""
      ? data.daysRemaining
      : data.trialDaysRemaining != null && data.trialDaysRemaining !== ""
        ? data.trialDaysRemaining
        : null;
  let daysRemaining = rawDays != null ? Number(rawDays) : null;
  if (daysRemaining != null && Number.isNaN(daysRemaining)) {
    daysRemaining = null;
  }

  let trialing =
    data.trialing != null
      ? !!data.trialing
      : data.isTrialing != null
        ? !!data.isTrialing
        : subscriptionStatus === "trialing";
  // If the API only sends trial day count while still entitled, treat as trialing.
  // Do NOT infer trialing from daysRemaining alone when status is canceled/expired
  // (company trial-ended payloads send daysRemaining: 0 with active: false).
  if (
    !trialing &&
    daysRemaining != null &&
    !!data.active &&
    subscriptionStatus !== "canceled" &&
    subscriptionStatus !== "cancelled" &&
    subscriptionStatus !== "unpaid" &&
    subscriptionStatus !== "incomplete_expired"
  ) {
    trialing = true;
  }

  const trialEnd = data.trialEnd ?? null;
  const currentPeriodEnd = data.currentPeriodEnd ?? null;
  const endIso = trialEnd || (trialing ? currentPeriodEnd : null);

  if (daysRemaining == null && endIso && trialing) {
    const ms = new Date(endIso).getTime() - Date.now();
    daysRemaining = Number.isFinite(ms)
      ? Math.max(0, Math.ceil(ms / (24 * 60 * 60 * 1000)))
      : null;
  }

  const trialPeriodDays = Number(data.trialPeriodDays);

  return {
    ...data,
    subscriptionStatus,
    trialing,
    trialEnd,
    currentPeriodEnd,
    daysRemaining,
    trialDaysRemaining: daysRemaining,
    trialPeriodDays: Number.isFinite(trialPeriodDays) && trialPeriodDays > 0 ? trialPeriodDays : null,
    clockOffsetMs: clockOffsetFromServer(data.serverNow),
  };
}

// App, cockpit and Messages ask at the same moment; the server answers from
// Stripe each time, so callers that overlap share one request.
let subscriptionStatusInFlight = null;

export function getSubscriptionStatus() {
  if (subscriptionStatusInFlight) return subscriptionStatusInFlight;
  subscriptionStatusInFlight = (async () => {
    try {
      const data = await billingRequest(
        "/payment/subscription/status",
        "GET",
        null,
        "Failed to fetch subscription status"
      );
      return normalizeSubscriptionStatus(data);
    } finally {
      subscriptionStatusInFlight = null;
    }
  })();
  return subscriptionStatusInFlight;
}

export async function cancelSubscription(immediately = false) {
  return billingRequest(
    "/payment/subscription/cancel",
    "POST",
    { immediately },
    "Failed to cancel subscription"
  );
}

export async function switchPlan(plan) {
  return billingRequest("/payment/subscription/switch", "POST", { plan }, "Failed to switch plan");
}

export async function resumeSubscription() {
  return billingRequest(
    "/payment/subscription/resume",
    "POST",
    {},
    "Failed to resume subscription"
  );
}

export async function cancelScheduledSwitch() {
  return billingRequest(
    "/payment/subscription/cancel-switch",
    "POST",
    {},
    "Failed to cancel scheduled switch"
  );
}

export async function fetchAndStoreSubscriptionStatus(dispatch) {
  try {
    dispatch({ type: SET_SUBSCRIPTION_LOADING, payload: true });
    const status = await getSubscriptionStatus();
    dispatch({ type: SET_SUBSCRIPTION_STATUS, payload: status });
    return status;
  } catch (e) {
    console.log("[payment] fetchSubscriptionStatus error:", safeMessage(e, "error"));
    dispatch({ type: SET_SUBSCRIPTION_LOADING, payload: false });
    return null;
  }
}

const TRIAL_SETTLE_ATTEMPTS = 10;
const TRIAL_SETTLE_INTERVAL_MS = 3000;
let trialSettleInFlight = null;

/**
 * The trial countdown reached zero. Stripe ends the trial on its own clock and
 * the webhook lands a few seconds later, so poll until the status stops
 * reporting a trial. Shared across callers — every mounted countdown hits zero
 * at the same moment.
 */
export function settleEndedTrial(dispatch) {
  if (trialSettleInFlight) return trialSettleInFlight;
  trialSettleInFlight = (async () => {
    try {
      for (let i = 0; i < TRIAL_SETTLE_ATTEMPTS; i += 1) {
        const status = await fetchAndStoreSubscriptionStatus(dispatch);
        if (status && !status.trialing) return status;
        await new Promise((resolve) => setTimeout(resolve, TRIAL_SETTLE_INTERVAL_MS));
      }
      return null;
    } finally {
      trialSettleInFlight = null;
    }
  })();
  return trialSettleInFlight;
}

/**
 * Kjuup+ package booking of a company: package, seats, and — for company
 * admins — price, invoices and the cancellation request.
 */
export async function getKjuupPlusBooking(companyId) {
  return billingRequest(`/company/${companyId}/kjuup-plus`, "GET", null, "Failed to load the Kjuup+ booking");
}

/** Company admin: ask Kjuup to cancel Kjuup+ (a super admin confirms it). */
export async function requestKjuupPlusCancel(companyId, note) {
  return billingRequest(
    `/company/${companyId}/kjuup-plus/cancel-request`,
    "POST",
    { note: note || null },
    "Failed to request the cancellation"
  );
}

export async function withdrawKjuupPlusCancel(companyId) {
  return billingRequest(
    `/company/${companyId}/kjuup-plus/cancel-request/withdraw`,
    "POST",
    {},
    "Failed to withdraw the cancellation request"
  );
}
